import React, { useState, ChangeEvent, useEffect } from "react";
import { FundraiserSubsequentInfo } from "../../../../Interfaces/Fundraiser";
import {
  PrimaryDivisionCreation,
  SecondaryDivisionCreation,
} from "../../../../Interfaces/Division";
import "./PrimarySecondaryDivisionForm.css";
import bear_pointing from "../../../../images/Starting-Your-Fundraiser/bear-pointing.png";
import explainingBear from "../../../../images/Starting-Your-Fundraiser/explaining-bear.png";
import { useNavigate } from "react-router-dom";
import {
  sanitizeTextInput,
  sanitizeEmailInput,
} from "../../../../CommonFunctions";
import config from "../../../../config";
import fetchWithAuth from "../../../../services/fetchWithAuth";

const BASE_API = config.API_BASE_URL;

type DivisionProps = {
  primary: PrimaryDivisionCreation;
  index: number;
  secondaryDivisionName: string;
  teamAdminName: string;
  onAddSecondaryDivision: (primaryId: number) => void;
  onRemoveSecondaryDivision: (primaryId: number, secondaryId: number) => void;
  onRemovePrimaryDivision: (primaryId: number) => void;
  onUpdatePrimaryDivision: (
    primaryId: number,
    field: "division_name",
    value: string,
  ) => void;
  onUpdateSecondaryDivision: (
    primaryId: number,
    secondaryId: number,
    field: SecondaryDivisionField,
    value: string,
  ) => void;
  isOnInitially?: boolean;
};

type SecondaryDivisionField = "division_name" | "admin_email";
export const DivisionExplanation: React.FC = () => {
  return (
    <div className="primary-secondary-form-division-container">
      <div className="primary-secondary-form-division-content">
        <h1 className="primary-secondary-form-division-title">
          Primary & Secondary Divisions
        </h1>
        <p className="primary-secondary-form-division-paragraph">
          <strong>Primary Divisions:</strong> Create main categories like grade
          levels (e.g., First Grade, Second Grade) to organize your fundraising
          efforts efficiently.
        </p>
        <p className="primary-secondary-form-division-paragraph">
          <strong>Secondary Divisions:</strong> Add specific groups or classes
          within each Primary Division for detailed management and tracking.
        </p>
      </div>
      <div className="primary-secondary-form-division-image-container">
        <img
          src={explainingBear}
          alt="Bear"
          className="primary-secondary-form-division-image"
        />
      </div>
    </div>
  );
};

const CollapsibleDivision: React.FC<DivisionProps> = ({
  primary,
  index,
  secondaryDivisionName,
  teamAdminName,
  onAddSecondaryDivision,
  onRemoveSecondaryDivision,
  onRemovePrimaryDivision,
  onUpdatePrimaryDivision,
  onUpdateSecondaryDivision,
  isOnInitially = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(isOnInitially);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  const handlePrimaryFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onUpdatePrimaryDivision(primary.frontend_id, "division_name", value);
  };

  const handleSecondaryFieldChange =
    (secondaryId: number, field: SecondaryDivisionField) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      onUpdateSecondaryDivision(primary.frontend_id, secondaryId, field, value);
    };

  return (
    <div>
      <div className="primary-secondary-form-primary-division">
        <div className="primary-secondary-form-primary-division-content">
          <div className="primary-secondary-form-circle">{index + 1}</div>
          <input
            type="text"
            value={primary.division_name}
            onChange={handlePrimaryFieldChange}
            className="primary-secondary-form-primary-division-input"
            placeholder="Enter division name"
          />
        </div>
        <div className="primary-secondary-form-primary-division-buttons">
          <button
            type="button"
            className="primary-secondary-form-expand-button"
            onClick={toggleExpansion}
          >
            {isExpanded ? "Hide Details" : "Show Details"}
            <span className="arrow">{isExpanded ? "▲" : "▼"}</span>
          </button>
          <button
            type="button"
            className="primary-secondary-form-remove-primary-button"
            onClick={() => onRemovePrimaryDivision(primary.frontend_id)}
          >
            Remove
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="primary-secondary-form-secondary-divisions">
          <div className="primary-secondary-form-secondary-division-header">
            <h3>{secondaryDivisionName}</h3>
            <button
              type="button"
              className="primary-secondary-form-add-new-button"
              onClick={() => onAddSecondaryDivision(primary.frontend_id)}
            >
              Add +
            </button>
          </div>
          <table className="primary-secondary-form-secondary-table">
            <thead>
              <tr>
                <th>SI No</th>
                <th>{secondaryDivisionName} Name</th>
                <th>{teamAdminName} Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {primary.secondary_divisions.map((item, secIndex) => (
                <tr key={item.frontend_id}>
                  <td>{secIndex + 1}</td>
                  <td>
                    <input
                      type="text"
                      value={item.division_name}
                      onChange={handleSecondaryFieldChange(
                        item.frontend_id,
                        "division_name",
                      )}
                      required
                    />
                  </td>
                  <td>
                    <input
                      type="email"
                      value={item.admin_email}
                      onChange={handleSecondaryFieldChange(
                        item.frontend_id,
                        "admin_email",
                      )}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="primary-secondary-form-remove-button"
                      onClick={() =>
                        onRemoveSecondaryDivision(
                          primary.frontend_id,
                          item.frontend_id,
                        )
                      }
                    >
                      -
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PrimarySecondaryDivisionForm: React.FC<FundraiserSubsequentInfo> = ({
  ...props
}) => {
  const navigate = useNavigate();
  const fundraiserID: number = props.fundraiserID;
  const teamAdminName = props.teamAdminName || "Team Admin";
  const primaryDivisionName = props.primaryDivisionName || "Primary Division";
  const secondaryDivisionName =
    props.secondaryDivisionName || "Secondary Division";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const sanitizedDivisions = divisions.map((division) => ({
      ...division,
      secondary_divisions: division.secondary_divisions.map((sec) => ({
        ...sec,
        admin_email: (() => {
          try {
            return sec.admin_email?.trim()
              ? sanitizeEmailInput(sec.admin_email.trim())
              : null;
          } catch (error) {
            if (error instanceof Error) {
              console.error("Invalid admin email:", error.message);
            } else {
              console.error("Unknown error during email sanitization:", error);
            }
            return null;
          }
        })(),
      })),
    }));

    const api_content = {
      fundraiser_id: fundraiserID,
      divisions: sanitizedDivisions,
    };
    const url = `${BASE_API}divisions/bulk-create`;

    const response = await fetchWithAuth(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(api_content),
    });

    navigate("/dashboard/user");
  };

  const generateId = (): number => {
    return Date.now();
  };

  const [divisions, setDivisions] = useState<PrimaryDivisionCreation[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchWithAuth(
          BASE_API + "division/fundraiser/" + fundraiserID,
        );
        const data: PrimaryDivisionCreation[] = await response.json();

        if (data.length > 0) {
          const transformedData: PrimaryDivisionCreation[] = data.map(
            (primary: any) => ({
              frontend_id: primary.frontend_id,
              division_name: primary.division_name,
              secondary_divisions: primary.secondary_divisions.map(
                (secondary: any) => ({
                  frontend_id: secondary.frontend_id,
                  division_name: secondary.division_name,
                  admin_email: secondary.admin_email,
                }),
              ),
            }),
          );
          setDivisions(transformedData);
        } else {
          // Use default data if API response is empty
          setDivisions([
            {
              frontend_id: generateId(),
              division_name: "Primary Division 1",
              secondary_divisions: [
                {
                  frontend_id: generateId(),
                  division_name: "Secondary Division 1",
                  admin_email: "",
                },
              ],
              toggle_on: true,
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        // Use default data on error
        setDivisions([
          {
            frontend_id: generateId(),
            division_name: "Primary Division 1",
            secondary_divisions: [
              {
                frontend_id: generateId(),
                division_name: "Secondary Division 1",
                admin_email: "",
              },
            ],
          },
        ]);
      }
    };

    fetchData();
  }, []);

  const addNewDivision = () => {
    const newId = generateId();
    const newDivision: PrimaryDivisionCreation = {
      frontend_id: newId,
      division_name: `Primary Division Sample`,
      secondary_divisions: [],
    };
    setDivisions([...divisions, newDivision]);
  };

  const addSecondaryDivision = (primaryId: number) => {
    const updatedDivisions = divisions.map((division) => {
      if (division.frontend_id === primaryId) {
        const newSecondaryId = generateId();
        const newSecondaryDivision: SecondaryDivisionCreation = {
          frontend_id: newSecondaryId,
          division_name: "",
          admin_email: "",
        };
        return {
          ...division,
          secondary_divisions: [
            ...division.secondary_divisions,
            newSecondaryDivision,
          ],
        };
      }
      return division;
    });
    setDivisions(updatedDivisions);
  };

  const removeSecondaryDivision = (primaryId: number, secondaryId: number) => {
    const updatedDivisions = divisions.map((division) => {
      if (division.frontend_id === primaryId) {
        return {
          ...division,
          secondary_divisions: division.secondary_divisions.filter(
            (sec) => sec.frontend_id !== secondaryId,
          ),
        };
      }
      return division;
    });
    setDivisions(updatedDivisions);
  };

  const removePrimaryDivision = (primaryId: number) => {
    const updatedDivisions = divisions.filter(
      (division) => division.frontend_id !== primaryId,
    );
    setDivisions(updatedDivisions);
  };

  const updatePrimaryDivision = (
    primaryId: number,
    field: "division_name",
    value: string,
  ) => {
    const sanitizedValue = sanitizeTextInput(value);
    const updatedDivisions = divisions.map((division) => {
      if (division.frontend_id === primaryId) {
        return {
          ...division,
          [field]: sanitizedValue,
        };
      }
      return division;
    });
    setDivisions(updatedDivisions);
  };

  const updateSecondaryDivision = (
    primaryId: number,
    secondaryId: number,
    field: SecondaryDivisionField,
    value: string,
  ) => {
    const sanitizedValue = sanitizeTextInput(value);
    const updatedDivisions = divisions.map((division) => {
      if (division.frontend_id === primaryId) {
        return {
          ...division,
          secondary_divisions: division.secondary_divisions.map((sec) => {
            if (sec.frontend_id === secondaryId) {
              return {
                ...sec,
                [field]: sanitizedValue,
              };
            }
            return sec;
          }),
        };
      }
      return division;
    });
    setDivisions(updatedDivisions);
  };

  return (
    <div className="primary-secondary-form-form-wrapper">
      <div className="primary-secondary-form-form-container">
        <form onSubmit={handleSubmit}>
          <DivisionExplanation />
          <div className="primary-secondary-form-header-container">
            <h1>{primaryDivisionName}</h1>
            <button
              type="button"
              className="primary-secondary-form-add-new-division-button"
              onClick={addNewDivision}
            >
              Add +
            </button>
          </div>
          {divisions.map((division, index) => (
            <CollapsibleDivision
              key={division.frontend_id}
              primary={division}
              index={index}
              secondaryDivisionName={secondaryDivisionName}
              teamAdminName={teamAdminName}
              onAddSecondaryDivision={addSecondaryDivision}
              onRemoveSecondaryDivision={removeSecondaryDivision}
              onRemovePrimaryDivision={removePrimaryDivision}
              onUpdatePrimaryDivision={updatePrimaryDivision}
              onUpdateSecondaryDivision={updateSecondaryDivision}
              isOnInitially={division.toggle_on || false}
            />
          ))}
          <div className="primary-secondary-form-submit-container">
            <img
              src={bear_pointing}
              alt="bear-pointing"
              className="centered-image"
            />
            <input
              type="submit"
              value="Save & Next"
              className="primary-secondary-form-submit-button"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default PrimarySecondaryDivisionForm;
